import { useCallback, useRef } from 'react';
import { checkboxColors } from '@/utils/colorUtils';

interface UseColorAssignmentReturn {
  getColor: (id: string) => number | undefined;
  reset: () => void;
}

/**
 * Assigns a stable color index from `checkboxColors` to each selected ID.
 * Colors are assigned incrementally on first selection and reused if the
 * same ID is re-selected after being deselected.
 */
const useColorAssignment = (selectedIds: string[]): UseColorAssignmentReturn => {
  const stableColors = useRef(new Map<string, number>());
  const nextColorIndex = useRef(0);

  for (const id of selectedIds) {
    if (!stableColors.current.has(id)) {
      const color = nextColorIndex.current % checkboxColors.length;
      stableColors.current.set(id, color);
      nextColorIndex.current += 1;
    }
  }

  const getColor = useCallback(
    (id: string): number | undefined => stableColors.current.get(id),
    []
  );

  const reset = useCallback(() => {
    stableColors.current.clear();
    nextColorIndex.current = 0;
  }, []);

  return { getColor, reset };
};

export { useColorAssignment };
export type { UseColorAssignmentReturn };
